import React, { useEffect, useState } from 'react';
import { aiApi, aiError, blocksOf, useAiStatus } from '../../lib/ai';
import './Ai.css';

/**
 * "Summarise" on a ticket's conversation (wave 3 batch 5).
 *
 * Offered on a ticket with 8 messages or more, to the people GET /ai/status
 * allows (IT staff and administrators by default; all staff when the switch
 * says so; never a contractor). What was reported, what was tried, where it
 * stands, next steps.
 *
 * The server keeps one per VISIBILITY LEVEL: somebody who may read private
 * notes is shown the one built with them (and told so); anybody else the
 * one built from the public thread only - never the other. It is kept until
 * a new message, and then shown as out of date with "Summarise again".
 *
 *   ticketId   the ticket
 *   messages   how many messages this person can see - the button waits
 *              for 8, and a new one makes the summary stale
 */
export default function TicketSummary({ ticketId, messages }) {
  const status = useAiStatus();
  const allowed = Boolean(status?.can?.summarise);
  const min = status?.min_messages || 8;
  const [data, setData] = useState(null);         // GET's answer
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  useEffect(() => {
    if (!allowed || messages < min) return undefined;
    let live = true;
    aiApi.summary(ticketId).then((d) => { if (live) setData(d); }).catch(() => { if (live) setData(null); });
    return () => { live = false; };
  }, [allowed, ticketId, messages, min]);

  if (!allowed || messages < min) return null;

  const make = async () => {
    setBusy(true);
    setProblem(null);
    try {
      setData(await aiApi.summarise(ticketId));
    } catch (err) {
      setProblem(aiError(err, 'Could not summarise this ticket.'));
    } finally {
      setBusy(false);
    }
  };

  const s = data?.summary;
  const stale = s && !s.fresh;
  return (
    <section className="tk-card ai-summary" aria-label="Summary" data-ai="summary">
      <div className="tk-card-head">
        <h2 className="tk-card-title"><span className="ai-badge" title="Written by Claude (AI)">AI</span> Summary</h2>
        <button type="button" className="tk-btn tk-btn-quiet" onClick={make} disabled={busy} data-ai="summarise">
          {busy ? 'Summarising…' : s ? (stale ? 'Summarise again' : 'Refresh') : 'Summarise'}
        </button>
      </div>
      {!s && !busy && <p className="tk-quiet">What was reported, what was tried, where it stands and what is next - in a few lines.</p>}
      {s && (
        <>
          <div className="ai-summary-body">
            {blocksOf(s.body).reduce((acc, b, i) => {
              // Consecutive list items into one list.
              if (b.kind === 'li') {
                const last = acc[acc.length - 1];
                if (last && last.type === 'ul') { last.items.push(b.text); return acc; }
                acc.push({ type: 'ul', items: [b.text], key: i });
                return acc;
              }
              acc.push({ type: b.kind, text: b.text, key: i });
              return acc;
            }, []).map((b) => (b.type === 'ul'
              ? <ul key={b.key}>{b.items.map((t, j) => <li key={j}>{t}</li>)}</ul>
              : b.type === 'h' ? <h4 key={b.key}>{b.text}</h4> : <p key={b.key}>{b.text}</p>))}
          </div>
          <p className="ai-meta">
            {stale ? 'Out of date - there are new messages since. ' : ''}
            From {s.messages} message{s.messages === 1 ? '' : 's'}
            {s.includes_private ? <> · <span className="ai-private">uses private notes - only people who can read them see this</span></> : null}
            {' · '}Written by Claude; check it against the thread.
          </p>
        </>
      )}
      {problem && <p className="ai-problem" role="alert">{problem}</p>}
    </section>
  );
}
